import React, { useState } from "react";
import { Button } from "../common/Button";
import { Input } from "../common/Input";
import { LoadingSpinner } from "../common/LoadingSpinner";

const CREATE_PAYMENT_ENDPOINTS = [
  `${import.meta.env.VITE_BACKEND_API_BASE_URL || ""}/create-payment.php`,
  `${import.meta.env.VITE_AUTH_API_BASE_URL || ""}/create-payment.php`,
  "https://gainwisefx.com/api/create-payment.php",
  "/api/create-payment.php",
].filter((value, index, list) => value && list.indexOf(value) === index);

const PaymentForm = ({
  onPaymentInitiated,
  defaultAmount = "",
  defaultDescription = "",
}) => {
  const [formData, setFormData] = useState({
    amount: defaultAmount,
    email: "",
    first_name: "",
    last_name: "",
    phone: "",
    description: defaultDescription,
    currency: "KES",
  });
  const [loading, setLoading] = useState(false);

  const showMessage = (message, type = "info") => {
    if (type === "error") {
      alert(`Error: ${message}`);
    } else if (type === "success") {
      alert(`Success: ${message}`);
    } else {
      alert(message);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const errors = [];

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      errors.push("Please enter a valid amount");
    }

    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.push("Please enter a valid email address");
    }

    if (!formData.first_name.trim()) {
      errors.push("First name is required");
    }

    if (!formData.last_name.trim()) {
      errors.push("Last name is required");
    }

    if (!formData.phone.trim()) {
      errors.push("Phone number is required");
    }

    if (!formData.description.trim()) {
      errors.push("Payment description is required");
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();
    if (errors.length > 0) {
      showMessage(errors.join(", "), "error");
      return;
    }

    setLoading(true);

    try {
      let data = null;

      for (const endpoint of CREATE_PAYMENT_ENDPOINTS) {
        try {
          const response = await fetch(endpoint, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(formData),
          });

          if (!response.ok) {
            continue;
          }

          data = await response.json();
          break;
        } catch {
          continue;
        }
      }

      if (!data) {
        throw new Error("Network error");
      }

      if (data.success) {
        showMessage(
          "Payment initiated successfully! Redirecting to payment page...",
          "success"
        );

        // Redirect to Pesapal payment page
        window.location.href = data.redirect_url;

        // Call callback if provided
        if (onPaymentInitiated) {
          onPaymentInitiated(data);
        }
      } else {
        showMessage(data.error || "Payment initiation failed", "error");
      }
    } catch (error) {
      console.error("Payment error:", error);
      showMessage("Network error. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
        Make Payment
      </h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="amount"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Amount *
          </label>
          <div className="relative">
            <Input
              id="amount"
              name="amount"
              type="number"
              step="0.01"
              min="1"
              value={formData.amount}
              onChange={handleInputChange}
              placeholder="Enter amount"
              className="pl-12"
              required
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-500 sm:text-sm">KES</span>
            </div>
          </div>
        </div>

        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Email Address *
          </label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="your@email.com"
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="first_name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              First Name *
            </label>
            <Input
              id="first_name"
              name="first_name"
              type="text"
              value={formData.first_name}
              onChange={handleInputChange}
              placeholder="John"
              required
            />
          </div>

          <div>
            <label
              htmlFor="last_name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Last Name *
            </label>
            <Input
              id="last_name"
              name="last_name"
              type="text"
              value={formData.last_name}
              onChange={handleInputChange}
              placeholder="Doe"
              required
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="phone"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Phone Number *
          </label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleInputChange}
            placeholder="0712345678"
            required
          />
        </div>

        <div>
          <label
            htmlFor="description"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Payment Description *
          </label>
          <textarea
            id="description"
            name="description"
            rows={3}
            value={formData.description}
            onChange={handleInputChange}
            placeholder="What is this payment for?"
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="w-full flex items-center justify-center"
        >
          {loading ? (
            <>
              <LoadingSpinner size="sm" className="mr-2" />
              Processing...
            </>
          ) : (
            "Proceed to Payment"
          )}
        </Button>
      </form>

      <div className="mt-6 text-center">
        <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
          <span>Secure payment powered by</span>
          <span className="font-semibold text-blue-600">Pesapal</span>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Supports Visa, Mastercard, M-Pesa, and other payment methods
        </p>
      </div>
    </div>
  );
};

export default PaymentForm;
